<!--
	`role`, `aria-modal`, the accessible name and the focus trap are all on THIS element. Binding any
	of them to an outer wrapper would leave assistive technology reading a role-less element, and a
	spec resolving that wrapper would still pass — the exact defect EFF-25623 shipped.

	The trap follows `modal` (through `trapsFocus()`), not left bare. `modal` is one claim — "the page
	behind this is inert" — and the keyboard is the third place it is made, beside the backdrop and
	`aria-modal`. A bare `effFocusTrap` takes the directive's `true` default and confines Tab inside a
	panel that has just disclaimed modality, which is a non-modal dialog trapping focus. A ported brain
	side panel is the one deliberate exception: it keeps `aria-modal` and the backdrop, as brain's
	`SidePanel` does, but its focus policy switches the trap off, because brain never confines Tab
	there — brain defect #37, reproduced by the spec's decision (Implementation Decisions §1).

	`aria-labelledby` and `aria-label` are computed so that exactly one of them is ever present: a
	panel with a heading is named by the heading the reader can see, and a panel without one falls
	back to the caller's string. Both at once is the Label-in-Name failure (WCAG 2.5.3), not extra
	care.
-->
<section
	#surface
	role="dialog"
	[effFocusTrap]="trapsFocus()"
	class="eff-clarity-fly-in-panel__surface"
	[attr.aria-modal]="ariaModal()"
	[attr.aria-labelledby]="ariaLabelledBy()"
	[attr.aria-label]="resolvedAriaLabel()"
	dsTestId="clarity-fly-in-surface"
>
	<!-- `horizontal="end"` carries the header on its own when there is no heading; when there is one,
		the heading's `grow` is what pushes the close control to the trailing edge. One close
		control, in one place: the panel owns dismissal, so a body rendered inside it never draws a
		second one. `closeDisabled` is the one state that switches it off — a panel that must not be
		walked away from, such as the reveal-once token, leaves closing to its body. -->
	<header vertical="center" horizontal="end" class="gap-sm eff-clarity-fly-in-panel__header">
		<!-- The body's way back (`ClarityFlyInRef.setHeaderLeading`), ahead of the heading and outside it. -->
		@if (headerLeading(); as leading) {
			<ng-container [ngTemplateOutlet]="leading" />
		}

		@if (resolvedTitle(); as panelTitle) {
			<!-- Wrapping, not truncated — the same reading as the dialog frame's heading, and for the
				same reason: a heading that wraps stays readable at 200% zoom and under the WCAG 1.4.12
				text-spacing overrides, and needs no tooltip to recover a hidden half. -->
			<!-- The subtitle sits under the heading, never inside it: the heading alone is the panel's accessible name. -->
			<div direction="column" class="min-w-0 grow">
				<h2
					class="eff-clarity-fly-in-panel__title"
					[class.eff-clarity-fly-in-panel__title--regular]="titleWeight() === 'regular'"
					[id]="titleElementId"
					dsTestId="clarity-fly-in-title"
				>
					{{ panelTitle }}
				</h2>

				@let panelSubtitle = resolvedSubtitle();
				@if (panelSubtitle) {
					<p class="text-body-sm text-on-surface-variant" dsTestId="clarity-fly-in-subtitle">
						{{ panelSubtitle }}
					</p>
				}
			</div>

			<!-- Beside the heading, never inside it: the heading is the panel's accessible name, and brain's
				`DialogChrome` keeps its count badge out of the title too. -->
			@let count = badgeCount();
			@if (count > 0) {
				<eff-badge tone="accent" shape="count" dsTestId="clarity-fly-in-count">{{ count }}</eff-badge>
			}
		}

		<!-- The body's own header controls (`ClarityFlyInRef.setHeaderActions`), outside the heading and right before
			the close, so the reading order is heading → actions → close. -->
		@if (headerActions(); as actions) {
			<ng-container [ngTemplateOutlet]="actions" />
		}

		<eff-icon-button
			variant="ghost"
			size="md"
			icon="close"
			[disabled]="closeDisabled()"
			[ariaLabel]="resolvedCloseLabel()"
			dsTestId="clarity-fly-in-close"
			(clicked)="onClose()"
		/>
	</header>

	<!-- No rule under the header: the design closes the band with the header's own shadow.

		The body, not the close control, takes the initial focus: the trap's first tabbable element is
		the close button, and landing there painted a focus ring on it the moment the panel opened.
		`effAutofocus` is the static marker the trap queries for; `tabindex="-1"` makes the scroll region
		focusable without adding it to the Tab order, so focus still starts inside the dialog and the
		arrow keys scroll the body.

		`ds-overscroll-y--contain` keeps the wheel inside the panel: without it, reaching the body's end
		chains the scroll out to the page behind, which then moves together with the panel. -->
	<div
		effAutofocus
		tabindex="-1"
		class="ds-scrollable--y ds-scrollable--compact ds-overscroll-y--contain eff-clarity-fly-in-panel__body"
		[class.eff-clarity-fly-in-panel__body--inset]="bodyInset()"
		dsTestId="clarity-fly-in-body"
	>
		<ng-container [ngComponentOutlet]="content()" [ngComponentOutletInjector]="contentInjector()" />
	</div>

	<!-- Absent entirely when the caller states no footer: the region is what pins the panel's actions
		below the scroll, so rendering an empty one would draw a rule under nothing. The footer is
		created with the BODY's injector, so it reads `ClarityFlyInRef` and `CLARITY_OVERLAY_DATA_TOKEN`
		exactly as the body does and can close the panel itself. -->
	@if (footer(); as footerComponent) {
		<mat-divider class="shrink-0" />

		<footer class="eff-clarity-fly-in-panel__footer">
			<ng-container [ngComponentOutlet]="footerComponent" [ngComponentOutletInjector]="contentInjector()" />
		</footer>
	}

	<!-- The body's own footer controls (`ClarityFlyInRef.setFooterActions`), pinned below the scroll in the action
		fly-in's footer band — absent, like the caller's footer, while the body sets none. -->
	@if (footerActions(); as actions) {
		<footer horizontal="end" vertical="center" class="eff-clarity-fly-in-panel__actions gap-md">
			<ng-container [ngTemplateOutlet]="actions" />
		</footer>
	}

	<eff-clarity-fly-in-resize-handle
		[panel]="surface"
		[minWidth]="minResizeWidth()"
		dsTestId="clarity-fly-in-resize-handle"
		(resized)="onResized($event)"
	/>
</section>
